"use client";

import Link from "next/link";
import { useMemo, useState } from "react";
import { TRUSTPILOT_REVIEW_URL } from "@/components/screen/start-order/components/Checkout/constants";

const LANGUAGES = [
  "English",
  "Spanish",
  "French",
  "German",
  "Chinese (Simplified)",
  "Arabic",
  "Portuguese",
  "Russian",
  "Japanese",
  "Korean",
  "Hindi",
  "Italian",
];

const PRICES = {
  certifiedPerPage: 24.5,
  standardPerWord: 0.1,
  // Notary matches Stripe canonical price (PROD-2026-05-18-007).
  notary: 19.95,
  mail: 15,
  urgentMultiplier: 1.5,
};

const FAQS = [
  {
    q: "How much does professional translation typically cost?",
    paragraphs: [
      "The typical cost for translation services starts at just $0.10 per word with Languex, while some companies charge from $0.15 to $0.35 per word. This price can vary, however, based on factors like language pairs, the complexity of the content, and turnaround time. For a customized estimate that fits your needs, we recommend using our Translation Cost Calculator or contacting our team for an official quote.",
    ],
    linkLabel: "Learn more",
    linkHref: "/pricing/",
  },
  {
    q: "What factors affect the cost of professional translation services",
    paragraphs: [
      "Language Pair: Some language combinations are pricier.",
      "Material Complexity: Technical content might have higher rates.",
      "Accuracy Level: Detailed translations can cost more.",
      "Turnaround Time: Faster services might come at a premium.",
      "Translator's Expertise: Veterans in the field often charge more.",
      "Translator Availability: Scarce language pairs can be pricier.",
      "Add-Ons: Services like SEO or proofreading can add to the cost.",
      "Do you need specialized talent or have a more complex project? Provide your project details and we'll respond within 24-hours or less.",
    ],
    linkLabel: "Get quote",
    linkHref: "/get-quote",
  },
  {
    q: "How much does translating a website into multiple languages cost?",
    paragraphs: [
      "The cost of website translation can vary, often starting at $500 for a smaller website. However, Languex's website translation services cost a competitive rate of $0.10 per word for standard content. Note that this rate doesn't include technical localization, usually billed by the hour based on the localization engineer's time.",
    ],
    linkLabel: "Learn more",
    linkHref: "/documents/website",
  },
  {
    q: "How much does it cost to translate an app",
    paragraphs: [
      "At Languex, the cost for standard app translation starts at just $0.10 per word. Keep in mind that extra charges may apply for proofreading and SEO services.",
    ],
    linkLabel: "Learn more",
    linkHref: "/documents/app",
  },
  {
    q: "How much does it cost to translate a game?",
    paragraphs: [
      "Standard game content translation at Languex starts at $0.10 per word. Additional costs for services such as proofreading and SEO optimization may apply.",
    ],
    linkLabel: "Learn more",
    linkHref: "/documents/game",
  },
  {
    q: "How much does it cost to translate a video?",
    paragraphs: [
      "When a transcript is provided, our starting rate is $0.10 per word for translation. This price doesn't include costs for transcription and subtitling. Use our translation cost calculator or request an official quote from our team for a more accurate cost.",
    ],
    linkLabel: "Learn more",
    linkHref: "/documents/video",
  },
  {
    q: "How much does it cost to translate an audio file?",
    paragraphs: [
      "With a client-provided transcript, translation costs start at $0.10 per word. Note that this price does not include transcription costs. For precise pricing, use our translation cost calculator or contact our team for an official quote.",
    ],
    linkLabel: "Learn more",
    linkHref: "/documents/speech",
  },
];

const formatMoney = (value: number) => `$${value.toFixed(2)}`;

export default function CostCalculatorPage() {
  const [sourceLanguage, setSourceLanguage] = useState("English");
  const [targetLanguage, setTargetLanguage] = useState("Spanish");
  const [translationType, setTranslationType] = useState<"certified" | "standard">(
    "certified"
  );
  const [quantity, setQuantity] = useState(1);
  const [urgent, setUrgent] = useState(false);
  const [notarized, setNotarized] = useState(false);
  const [mailedCopies, setMailedCopies] = useState(false);
  const [description, setDescription] = useState("");

  const { subtotal, urgentFee, notaryFee, mailFee, total, eta } = useMemo(() => {
    const base =
      translationType === "certified"
        ? PRICES.certifiedPerPage * quantity
        : PRICES.standardPerWord * quantity;
    const uFee = urgent ? base * (PRICES.urgentMultiplier - 1) : 0;
    const nFee = notarized ? PRICES.notary : 0;
    const mFee = mailedCopies ? PRICES.mail : 0;
    const t = base + uFee + nFee + mFee;

    let turnaround = "2-3 business days";
    if (urgent) {
      turnaround = "Within 24 hours";
    } else if (translationType === "standard") {
      turnaround = quantity < 1000 ? "1-2 business days" : quantity < 5000 ? "2-3 business days" : "3-5 business days";
    } else {
      turnaround = quantity <= 5 ? "2-3 business days" : quantity <= 20 ? "3-4 business days" : "5-7 business days";
    }

    if (mailedCopies) {
      turnaround += " (+ shipping)";
    }

    return {
      subtotal: base,
      urgentFee: uFee,
      notaryFee: nFee,
      mailFee: mFee,
      total: t,
      eta: turnaround,
    };
  }, [translationType, quantity, urgent, notarized, mailedCopies]);

  const quantityLabel = translationType === "certified" ? "Pages" : "Words";
  const addons = [
    urgent ? "Urgent" : null,
    notarized ? "Notarized" : null,
    mailedCopies ? "Mailed copies" : null,
  ].filter(Boolean) as string[];

  const orderHref = useMemo(() => {
    const params = new URLSearchParams({
      source: sourceLanguage,
      target: targetLanguage,
      type: translationType,
      qty: String(quantity),
      urgent: String(urgent),
      notarized: String(notarized),
      mailed: String(mailedCopies),
    });
    if (description.trim()) {
      params.set("desc", description.trim());
    }
    return `/order?${params.toString()}`;
  }, [
    sourceLanguage,
    targetLanguage,
    translationType,
    quantity,
    urgent,
    notarized,
    mailedCopies,
    description,
  ]);

  return (
    <main className="bg-[#F5F7FA] text-[#1B1F3B]">
      <section className="border-b border-[#E2ECF1] bg-gradient-to-b from-[#EAEFFB] to-[#F5F7FA] px-4 py-12 md:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#E2ECF1] bg-white px-4 py-2 text-sm font-semibold text-[#1F559E]">
            <span className="h-2 w-2 rounded-full bg-[#34AD68]" /> Free instant quote · No signup required
          </span>
          <h1 className="mt-4 text-3xl font-bold tracking-tight md:text-5xl">
            Translation cost calculator
            <span className="block text-[#2F80ED]">— quote in 30 seconds</span>
          </h1>
          <p className="mt-4 text-base text-[#37474F] md:text-lg">
            Get an accurate quote for certified or standard translation across 100+ languages - pricing transparent, no hidden fees, ready in as little as 24 hours.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3 text-xs text-[#37474F] md:gap-6 md:text-sm">
            {/*
              2026-05-25 — TrustPilot mention is now an outbound link to
              the live review page. Was a plain <span>; turning it into
              an anchor lets customers verify the claim with one click.
            */}
            <a
              href={TRUSTPILOT_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#1F559E] hover:underline transition-colors"
            >
              ★★★★★ <strong>4.9/5</strong> on TrustPilot
            </a>
            <span><strong>50,000+</strong> customers translated</span>
            <span><strong>100+</strong> languages</span>
            <span><strong>USCIS-accepted</strong></span>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-8 md:px-8 md:py-10">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.35fr_1fr]">
          <div className="overflow-hidden rounded-2xl border border-[#E2ECF1] bg-white shadow-sm">
            <div className="space-y-8 p-6 md:p-8">
              <div>
                <h2 className="text-xl font-semibold">1. Select languages</h2>
                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                  <select
                    value={sourceLanguage}
                    onChange={(e) => setSourceLanguage(e.target.value)}
                    className="rounded-xl border border-[#E2ECF1] px-4 py-3"
                  >
                    {LANGUAGES.map((lang) => (
                      <option key={`source-${lang}`} value={lang}>
                        {lang}
                      </option>
                    ))}
                  </select>
                  <select
                    value={targetLanguage}
                    onChange={(e) => setTargetLanguage(e.target.value)}
                    className="rounded-xl border border-[#E2ECF1] px-4 py-3"
                  >
                    {LANGUAGES.map((lang) => (
                      <option key={`target-${lang}`} value={lang}>
                        {lang}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between gap-4">
                  <h2 className="text-xl font-semibold">2. Translation type</h2>
                  <p className="hidden text-xs text-[#A5BBD9] md:block">
                    Not sure? Most personal documents need certified.
                  </p>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => {
                      setTranslationType("certified");
                      if (quantity > 50) setQuantity(1);
                    }}
                    className={`rounded-xl border-2 p-4 text-left ${
                      translationType === "certified"
                        ? "border-[#2F80ED] bg-[#F5FAFF]"
                        : "border-[#E2ECF1]"
                    }`}
                  >
                    <p className="text-lg font-semibold">Certified</p>
                    <p className="text-sm font-semibold text-[#34AD68]">From $24.50/page</p>
                    <p className="mt-1 text-sm text-[#37474F]">Best for USCIS, legal, and official use.</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setTranslationType("standard");
                      if (quantity < 100) setQuantity(500);
                    }}
                    className={`rounded-xl border-2 p-4 text-left ${
                      translationType === "standard"
                        ? "border-[#2F80ED] bg-[#F5FAFF]"
                        : "border-[#E2ECF1]"
                    }`}
                  >
                    <p className="text-lg font-semibold">Standard</p>
                    <p className="text-sm font-semibold text-[#34AD68]">From $0.10/word</p>
                    <p className="mt-1 text-sm text-[#37474F]">Best for business and general content.</p>
                  </button>
                </div>
              </div>

              <div>
                <h2 className="text-xl font-semibold">3. Project details</h2>
                <div className="mt-4 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      setQuantity((v) =>
                        Math.max(1, v - (translationType === "standard" ? 50 : 1))
                      )
                    }
                    className="h-11 w-11 rounded-lg border border-[#E2ECF1] bg-[#F5F7FA] text-xl"
                  >
                    -
                  </button>
                  <input
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
                    className="h-11 w-36 rounded-lg border border-[#E2ECF1] px-4 text-center font-semibold"
                    type="number"
                    min={1}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setQuantity((v) => v + (translationType === "standard" ? 50 : 1))
                    }
                    className="h-11 w-11 rounded-lg border border-[#E2ECF1] bg-[#F5F7FA] text-xl"
                  >
                    +
                  </button>
                </div>
                <p className="mt-2 text-xs text-[#A5BBD9]">
                  {translationType === "certified"
                    ? "A page is 250 words or fewer. Use Word/Google Docs word counter to estimate."
                    : "Standard translation is charged per word. Most pages contain ~250 words."}
                </p>

                <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
                  <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-[#E2ECF1] p-3">
                    <input
                      type="checkbox"
                      checked={urgent}
                      onChange={(e) => setUrgent(e.target.checked)}
                    />
                    <span className="text-sm font-medium">Urgent (+50%)</span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-[#E2ECF1] p-3">
                    <input
                      type="checkbox"
                      checked={notarized}
                      onChange={(e) => setNotarized(e.target.checked)}
                    />
                    <span className="text-sm font-medium">Notarized (+${PRICES.notary.toFixed(2)})</span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-[#E2ECF1] p-3">
                    <input
                      type="checkbox"
                      checked={mailedCopies}
                      onChange={(e) => setMailedCopies(e.target.checked)}
                    />
                    <span className="text-sm font-medium">Mailed copies (+$15)</span>
                  </label>
                </div>

                <input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What do you need translated? (optional)"
                  className="mt-5 w-full rounded-xl border border-[#E2ECF1] px-4 py-3"
                />
              </div>
            </div>
          </div>

          <aside className="sticky top-6 h-fit overflow-hidden rounded-2xl border border-[#E2ECF1] bg-white shadow-md">
            <div className="bg-gradient-to-r from-[#1B1F3B] to-[#1F559E] px-6 py-5 text-white">
              <p className="text-xs uppercase tracking-widest opacity-70">Your quote</p>
              <h3 className="text-xl font-semibold">Order summary</h3>
            </div>
            <div className="space-y-3 px-6 py-5">
              <div className="rounded-xl bg-[#F5F7FA] p-3 text-sm">
                <p>
                  <strong>From:</strong> {sourceLanguage}
                </p>
                <p>
                  <strong>To:</strong> {targetLanguage}
                </p>
              </div>
              <p className="flex justify-between text-sm">
                <span>Type</span>
                <strong>{translationType === "certified" ? "Certified" : "Standard"}</strong>
              </p>
              <p className="flex justify-between text-sm">
                <span>{quantityLabel}</span>
                <strong>{quantity.toLocaleString()}</strong>
              </p>
              {addons.length > 0 && (
                <p className="flex justify-between gap-4 text-sm">
                  <span>Add-ons</span>
                  <strong className="text-right">{addons.join(", ")}</strong>
                </p>
              )}
              <hr />
              <p className="flex justify-between text-sm">
                <span>Subtotal</span>
                <strong>{formatMoney(subtotal)}</strong>
              </p>
              {urgentFee > 0 && (
                <p className="flex justify-between text-sm">
                  <span>Urgent surcharge</span>
                  <strong>{formatMoney(urgentFee)}</strong>
                </p>
              )}
              {notaryFee > 0 && (
                <p className="flex justify-between text-sm">
                  <span>Notarization</span>
                  <strong>{formatMoney(notaryFee)}</strong>
                </p>
              )}
              {mailFee > 0 && (
                <p className="flex justify-between text-sm">
                  <span>Mailed copies</span>
                  <strong>{formatMoney(mailFee)}</strong>
                </p>
              )}
              <hr />
              <p className="flex items-end justify-between">
                <span className="text-sm font-semibold">Total</span>
                <strong className="text-3xl text-[#2F80ED]">{formatMoney(total)}</strong>
              </p>
              <p className="rounded-lg bg-[#F0FBF4] px-3 py-2 text-sm text-[#0E5A36]">
                Estimated delivery: <strong>{eta}</strong>
              </p>
              <Link
                href={orderHref}
                className="mt-2 inline-flex w-full items-center justify-center rounded-full bg-[#34AD68] px-5 py-3 text-sm font-semibold text-white hover:bg-[#0E5A36]"
              >
                Continue to order →
              </Link>
            </div>
            <div className="flex items-center justify-center gap-3 border-t border-[#E2ECF1] bg-[#F5F7FA] px-6 py-4 text-[11px] text-[#37474F]">
              <span>Secure checkout</span>
              <span>·</span>
              <span>
                <strong>Money-back</strong> guarantee
              </span>
            </div>
          </aside>
        </div>
      </section>

      <section className="border-y border-[#E2ECF1] bg-white px-4 py-6 md:px-8">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-4 text-center md:grid-cols-5">
          <div>
            <p className="text-2xl font-bold">50,000+</p>
            <p className="text-xs text-[#6B7280]">Customers served</p>
          </div>
          <div>
            <p className="text-2xl font-bold">100+</p>
            <p className="text-xs text-[#6B7280]">Languages</p>
          </div>
          {/*
            2026-05-25 — same as above: the TrustPilot stat in the
            5-up stats strip now links to the live review page.
          */}
          <a
            href={TRUSTPILOT_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="block hover:opacity-80 transition-opacity"
          >
            <p className="text-2xl font-bold">4.9★</p>
            <p className="text-xs text-[#6B7280] hover:text-[#1F559E]">
              TrustPilot rating
            </p>
          </a>
          <div>
            <p className="text-2xl font-bold">24h</p>
            <p className="text-xs text-[#6B7280]">Fast turnaround</p>
          </div>
          <div>
            <p className="text-2xl font-bold">100%</p>
            <p className="text-xs text-[#6B7280]">Acceptance guarantee</p>
          </div>
        </div>
      </section>

      <section className="px-4 py-14 md:px-8">
        <div className="mx-auto max-w-6xl">
          <p className="text-center text-xs font-semibold uppercase tracking-[0.12em] text-[#2F80ED]">
            Pricing transparency
          </p>
          <h2 className="text-center text-3xl font-bold">What affects your translation cost</h2>
          <p className="mx-auto mt-3 max-w-3xl text-center text-sm text-[#37474F]">
            Every project is unique. Here is how we calculate accurate pricing with no hidden fees.
          </p>
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            {[
              {
                title: "Translator specialization",
                desc: "Medical, legal, and technical content requires expert linguists.",
              },
              {
                title: "Dedicated project manager",
                desc: "Complex projects include stronger QA and hands-on coordination.",
              },
              {
                title: "Less common language pairs",
                desc: "Rare language combinations require specialized talent sourcing.",
              },
              {
                title: "Volume discounts",
                desc: "Larger projects can qualify for better per-word/per-page rates.",
              },
              {
                title: "Additional review",
                desc: "Extra proofreading can be added for critical documents.",
              },
              {
                title: "Certified or notarized",
                desc: "Certification and notary workflows add formal processing steps.",
              },
            ].map((item) => (
              <div key={item.title} className="rounded-xl border border-[#E2ECF1] bg-white p-5">
                <p className="font-semibold">{item.title}</p>
                <p className="mt-2 text-sm text-[#37474F]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-[#E2ECF1] bg-white px-4 py-10 md:px-8">
        <div className="mx-auto max-w-6xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#6B7280]">
            Trusted by global businesses
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-6 text-[#6B7280] md:gap-10">
            {["USCIS", "Google", "Stripe", "Airbnb", "Notion", "Shopify", "DocuSign"].map(
              (logo) => (
                <span key={logo} className="text-sm font-semibold md:text-base">
                  {logo}
                </span>
              )
            )}
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-14 md:px-8">
        <div className="mx-auto max-w-4xl">
          <p className="text-center text-xs font-semibold uppercase tracking-[0.12em] text-[#2F80ED]">
            FAQ
          </p>
          <h2 className="text-center text-3xl font-bold">Frequently asked questions</h2>
          <div className="mt-8 space-y-3">
            {FAQS.map((item, index) => (
              <details key={item.q} open={index === 0} className="rounded-xl border border-[#E2ECF1] bg-white p-4">
                <summary className="cursor-pointer font-semibold">{item.q}</summary>
                <div className="mt-2 space-y-2 text-sm text-[#37474F]">
                  {item.paragraphs.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                  <Link href={item.linkHref} className="inline-flex font-semibold text-[#2F80ED] hover:underline">
                    {item.linkLabel}
                  </Link>
                </div>
              </details>
            ))}
          </div>
          <div className="mt-8 text-center">
            <p className="text-sm text-[#37474F]">Have more questions? We have answers.</p>
            <Link
              href="/faqs/"
              className="mt-3 inline-flex items-center rounded-full border border-[#2F80ED] px-5 py-2 text-sm font-semibold text-[#2F80ED] hover:bg-[#F5FAFF]"
            >
              Explore FAQ
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 pb-10 md:px-8 md:pb-14">
        <div className="mx-auto max-w-6xl rounded-2xl bg-gradient-to-r from-[#1B1F3B] to-[#1F559E] px-6 py-8 text-white md:px-10">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#8FD0FF]">Ready when you are</p>
          <div className="mt-2 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-2xl font-bold">Have a complex or specialized project?</h3>
              <p className="mt-1 text-sm text-white/85">
                Get a tailored quote with a dedicated project manager. Response within 24 hours guaranteed.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                href="/support"
                className="inline-flex items-center rounded-full bg-[#34AD68] px-5 py-2 text-sm font-semibold text-white hover:bg-[#0E5A36]"
              >
                Talk to sales
              </Link>
              <Link
                href="/get-quote"
                className="inline-flex items-center rounded-full border border-white/40 px-5 py-2 text-sm font-semibold text-white hover:bg-white/10"
              >
                Get instant quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-[#E2ECF1] bg-white px-4 py-3 shadow-[0_-8px_20px_rgba(27,31,59,0.08)] md:hidden">
        <div className="mx-auto flex max-w-6xl items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#6B7280]">
              Estimated total
            </p>
            <p className="text-2xl font-bold text-[#1B1F3B]">{formatMoney(total)}</p>
            <p className="text-xs text-[#37474F]">{eta}</p>
          </div>
          <Link
            href={orderHref}
            className="inline-flex shrink-0 items-center rounded-full bg-[#34AD68] px-4 py-3 text-sm font-semibold text-white"
          >
            Continue →
          </Link>
        </div>
      </div>
    </main>
  );
}
